import React, { useState, useEffect, useCallback } from 'react';
import { openInAppFilePreview } from '../../utils/filePreviewBus';
import { toProtectedFileUrl } from '../../utils/secureFile';
import {
  Box, Typography, Button, Stack, CircularProgress, Alert, Snackbar,
  Card, CardContent, Paper, Chip, Checkbox, Divider, useMediaQuery,
  TextField, IconButton, Tooltip, Grid, Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import {
  ArrowBack, CheckCircle, LocalShipping, PhotoCamera, Receipt,
  Inventory2, Person, LocationOn, RequestQuote, DoneAll, Print, Refresh,
  Add, ExpandMore, Payments
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  getPembelianById, getBatches, createBatch, updateBatch,
  mintaPenagihanBatch, updatePembayaranBatch
} from '../API/supplierPenyedia';

const BASE_URL = process.env.REACT_APP_URL || 'http://localhost:5100';
const fotoUrl = (name) => (name ? toProtectedFileUrl(`pengiriman-batch/${name}`) : null);
const formatRp = (n) => (!n && n !== 0) ? '-' : 'Rp ' + Number(n).toLocaleString('id-ID');
const formatDate = (d) => d ? new Date(d).toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' }) : '-';

const PENGIRIMAN_COLOR = { 'Menunggu Respon': 'default', 'Diproses': 'info', 'Dikirim': 'warning', 'Sampai': 'success' };
const BAYAR_COLOR = { 'Belum Dibayar': 'error', 'Sudah Dibayar': 'success' };

const todayStr = () => new Date().toISOString().slice(0, 10);
const toDateInput = (d) => d ? new Date(d).toISOString().slice(0, 10) : '';

// Komponen kartu untuk satu batch pengiriman
const BatchCard = ({ batch, isSupplier, canManageBayar, onChanged, notify, allDetails }) => {
  const [expanded, setExpanded] = useState(true);
  const [fotoKirim, setFotoKirim] = useState(null);
  const [fotoSampai, setFotoSampai] = useState(null);
  const [fotoKirimPreview, setFotoKirimPreview] = useState('');
  const [fotoSampaiPreview, setFotoSampaiPreview] = useState('');
  const [tanggalKirim, setTanggalKirim] = useState(toDateInput(batch.tanggalKirim) || todayStr());
  const [tanggalSampai, setTanggalSampai] = useState(toDateInput(batch.tanggalSampai) || todayStr());
  // Catatan diisi baru pada tiap proses (tidak membawa nilai sebelumnya)
  const [catatanKirim, setCatatanKirim] = useState('');
  const [catatanSampai, setCatatanSampai] = useState('');
  const [saving, setSaving] = useState(false);

  const items = batch.Items || [];
  const totalBatch = batch.totalBatch || items.reduce((s, it) => s + (Number(it.DetailTransaksi?.totalTransaksi) || 0), 0);

  // Bersihkan object URL preview saat unmount / ganti file
  useEffect(() => () => {
    if (fotoKirimPreview) URL.revokeObjectURL(fotoKirimPreview);
    if (fotoSampaiPreview) URL.revokeObjectURL(fotoSampaiPreview);
  }, [fotoKirimPreview, fotoSampaiPreview]);

  const pickFotoKirim = (file) => {
    if (fotoKirimPreview) URL.revokeObjectURL(fotoKirimPreview);
    setFotoKirim(file);
    setFotoKirimPreview(file ? URL.createObjectURL(file) : '');
  };
  const pickFotoSampai = (file) => {
    if (fotoSampaiPreview) URL.revokeObjectURL(fotoSampaiPreview);
    setFotoSampai(file);
    setFotoSampaiPreview(file ? URL.createObjectURL(file) : '');
  };

  // Barang Diproses / Dikirim
  const saveKirim = async () => {
    if (!fotoKirim && !batch.fotoPengiriman) return notify('Foto barang diproses wajib diupload', 'warning');
    if (!tanggalKirim) return notify('Tanggal kirim wajib diisi', 'warning');
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('statusPengiriman', 'Dikirim');
      fd.append('tanggalKirim', tanggalKirim);
      if (catatanKirim.trim()) fd.append('catatanKirim', catatanKirim.trim());
      if (fotoKirim) fd.append('fotoPengiriman', fotoKirim);
      await updateBatch(batch.id, fd);
      notify('Barang diproses & dikirim');
      pickFotoKirim(null); setCatatanKirim('');
      onChanged();
    } catch (e) { notify(e.response?.data?.message || 'Gagal', 'error'); }
    finally { setSaving(false); }
  };

  // Barang Diterima / Sampai
  const saveSampai = async () => {
    if (!fotoSampai && !batch.fotoSampai) return notify('Foto barang diterima wajib diupload', 'warning');
    if (!tanggalSampai) return notify('Tanggal terima wajib diisi', 'warning');
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('statusPengiriman', 'Sampai');
      fd.append('tanggalSampai', tanggalSampai);
      if (catatanSampai.trim()) fd.append('catatanSampai', catatanSampai.trim());
      if (fotoSampai) fd.append('fotoSampai', fotoSampai);
      await updateBatch(batch.id, fd);
      notify('Barang diterima, pengiriman selesai');
      pickFotoSampai(null); setCatatanSampai('');
      onChanged();
    } catch (e) { notify(e.response?.data?.message || 'Gagal', 'error'); }
    finally { setSaving(false); }
  };

  const handleTagih = async () => {
    try { await mintaPenagihanBatch(batch.id); notify('Penagihan diajukan'); onChanged(); }
    catch (e) { notify(e.response?.data?.message || 'Gagal', 'error'); }
  };

  const handleBayar = async (val) => {
    try { await updatePembayaranBatch(batch.id, val); notify('Status pembayaran diperbarui'); onChanged(); }
    catch (e) { notify(e.response?.data?.message || 'Gagal', 'error'); }
  };

  const stepNum = batch.statusPengiriman === 'Sampai' ? 3
    : batch.statusPengiriman === 'Dikirim' ? 2 : 1;

  return (
    <Card variant="outlined" sx={{ mb: 2, border: batch.statusPengiriman === 'Sampai' ? '2px solid #4caf50' : '1px solid #e0e0e0' }}>
      <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ gap: 0.5, flex: 1 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              Pengiriman #{batch.batchKe}
            </Typography>
            <Chip size="small" icon={<LocalShipping sx={{ fontSize: 14 }} />} label={batch.statusPengiriman} color={PENGIRIMAN_COLOR[batch.statusPengiriman] || 'default'} />
            <Chip size="small" icon={<RequestQuote sx={{ fontSize: 14 }} />} label={batch.statusPembayaran} color={BAYAR_COLOR[batch.statusPembayaran] || 'default'} variant={batch.statusPembayaran === 'Sudah Dibayar' ? 'filled' : 'outlined'} />
            <Typography variant="caption" fontWeight={600} color="primary.main" sx={{ ml: 'auto' }}>
              {formatRp(totalBatch)}
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          {/* Daftar barang di batch ini */}
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Barang dikirim ({items.length}):
          </Typography>
          <Stack spacing={0.25} sx={{ mb: 1.5 }}>
            {items.map((it, i) => (
              <Stack key={it.id} direction="row" alignItems="center" spacing={0.5}
                sx={{ py: 0.25, px: 1, borderRadius: 1, bgcolor: '#e8f5e9' }}>
                <Typography variant="body2" sx={{ flex: 1 }}>{i + 1}. {it.DetailTransaksi?.namaBarang || '-'}</Typography>
                <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                  {it.DetailTransaksi?.kategoriPembelian && <Chip size="small" label={it.DetailTransaksi.kategoriPembelian} variant="outlined" color="primary" sx={{ fontSize: 9, height: 18 }} />}
                  <Typography variant="caption" color="text.secondary">{it.DetailTransaksi?.jumlahBarang} {it.DetailTransaksi?.satuan}</Typography>
                </Stack>
                <Typography variant="caption" fontWeight={600}>{formatRp(it.DetailTransaksi?.totalTransaksi)}</Typography>
              </Stack>
            ))}
          </Stack>

          {/* Step pengiriman */}
          <Stack direction="row" spacing={0.5} sx={{ mb: 2, flexWrap: 'wrap', gap: 0.5 }}>
            {['Menunggu', 'Barang Diproses', 'Barang Diterima'].map((label, i) => (
              <Chip key={i} size="small" label={label}
                color={i < stepNum ? 'success' : i === stepNum - 1 ? 'primary' : 'default'}
                variant={i < stepNum ? 'filled' : 'outlined'}
                sx={{ fontSize: 11 }} />
            ))}
          </Stack>

          {/* Proses oleh supplier: 2 tahap — Barang Diproses & Barang Diterima */}
          {isSupplier && batch.statusPengiriman !== 'Sampai' && (
            <Box sx={{ mb: 2, p: 1.5, border: '1px dashed #cbd5e1', borderRadius: 2, bgcolor: '#fafbfc' }}>
              {/* Tahap 1: Barang Diproses (Kirim) */}
              {(batch.statusPengiriman === 'Menunggu Respon' || batch.statusPengiriman === 'Diproses' || batch.statusPengiriman === 'Dikirim') && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                    <LocalShipping sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} /> Barang Diproses / Dikirim
                  </Typography>
                  {/* Preview foto: yang baru dipilih menang atas yang tersimpan */}
                  {(fotoKirimPreview || batch.fotoPengiriman) && (
                    <Box sx={{ mb: 1 }}>
                      <img src={fotoKirimPreview || fotoUrl(batch.fotoPengiriman)} alt="Foto barang diproses"
                        style={{ width: 140, height: 105, objectFit: 'cover', borderRadius: 6, border: '1px solid #ddd', cursor: 'pointer' }}
                        onClick={() => openInAppFilePreview({ url: fotoKirimPreview || fotoUrl(batch.fotoPengiriman), title: 'Foto Barang Diproses' })} />
                    </Box>
                  )}
                  <Grid container spacing={1} sx={{ mb: 1 }}>
                    <Grid item xs={12} sm={6}>
                      <Button component="label" variant="outlined" size="small" startIcon={<PhotoCamera />} fullWidth>
                        {fotoKirim ? fotoKirim.name : 'Upload Foto Barang Diproses'}
                        <input hidden type="file" accept="image/*" onChange={(e) => pickFotoKirim(e.target.files?.[0] || null)} />
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField size="small" type="date" label="Tanggal Kirim" fullWidth InputLabelProps={{ shrink: true }}
                        value={tanggalKirim} onChange={(e) => setTanggalKirim(e.target.value)} />
                    </Grid>
                  </Grid>
                  <TextField size="small" label="Catatan Pengiriman (isi baru)" fullWidth multiline rows={2}
                    placeholder="Tulis catatan proses pengiriman..." value={catatanKirim}
                    onChange={(e) => setCatatanKirim(e.target.value)} sx={{ mb: 1 }} />
                  <Button variant="contained" size="small" disabled={saving} onClick={saveKirim} startIcon={<LocalShipping />}>
                    {batch.statusPengiriman === 'Dikirim' ? 'Update Barang Diproses' : 'Kirim Barang'}
                  </Button>
                </Box>
              )}

              {/* Tahap 2: Barang Diterima (Sampai) */}
              {batch.statusPengiriman === 'Dikirim' && (
                <Box sx={{ mb: 1, pt: 1.5, borderTop: '1px solid #e2e8f0' }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                    <CheckCircle sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} /> Barang Diterima
                  </Typography>
                  {(fotoSampaiPreview || batch.fotoSampai) && (
                    <Box sx={{ mb: 1 }}>
                      <img src={fotoSampaiPreview || fotoUrl(batch.fotoSampai)} alt="Foto barang diterima"
                        style={{ width: 140, height: 105, objectFit: 'cover', borderRadius: 6, border: '1px solid #ddd', cursor: 'pointer' }}
                        onClick={() => openInAppFilePreview({ url: fotoSampaiPreview || fotoUrl(batch.fotoSampai), title: 'Foto Barang Diterima' })} />
                    </Box>
                  )}
                  <Grid container spacing={1} sx={{ mb: 1 }}>
                    <Grid item xs={12} sm={6}>
                      <Button component="label" variant="outlined" size="small" startIcon={<PhotoCamera />} fullWidth>
                        {fotoSampai ? fotoSampai.name : 'Upload Foto Barang Diterima'}
                        <input hidden type="file" accept="image/*" onChange={(e) => pickFotoSampai(e.target.files?.[0] || null)} />
                      </Button>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField size="small" type="date" label="Tanggal Terima" fullWidth InputLabelProps={{ shrink: true }}
                        value={tanggalSampai} onChange={(e) => setTanggalSampai(e.target.value)} />
                    </Grid>
                  </Grid>
                  <TextField size="small" label="Catatan Penerimaan (isi baru)" fullWidth multiline rows={2}
                    placeholder="Tulis catatan barang diterima..." value={catatanSampai}
                    onChange={(e) => setCatatanSampai(e.target.value)} sx={{ mb: 1 }} />
                  <Button variant="contained" size="small" color="success" disabled={saving} onClick={saveSampai} startIcon={<CheckCircle />}>
                    Konfirmasi Barang Diterima
                  </Button>
                </Box>
              )}
            </Box>
          )}

          {/* Info tanggal, catatan & foto (read-only / semua role) */}
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 0.5, mb: 1 }}>
            {batch.tanggalKirim && <Chip size="small" variant="outlined" label={`Dikirim: ${formatDate(batch.tanggalKirim)}`} />}
            {batch.tanggalSampai && <Chip size="small" variant="outlined" color="success" label={`Diterima: ${formatDate(batch.tanggalSampai)}`} />}
            {batch.fotoPengiriman && <Button size="small" onClick={() => openInAppFilePreview({ url: fotoUrl(batch.fotoPengiriman), title: 'Foto Barang Diproses' })} sx={{ textTransform: 'none' }}>Foto Diproses</Button>}
            {batch.fotoSampai && <Button size="small" onClick={() => openInAppFilePreview({ url: fotoUrl(batch.fotoSampai), title: 'Foto Barang Diterima' })} sx={{ textTransform: 'none' }}>Foto Diterima</Button>}
          </Stack>
          {(batch.catatanKirim || batch.catatanSampai || batch.catatanPengiriman) && (
            <Stack spacing={0.25} sx={{ mb: 1 }}>
              {batch.catatanKirim && <Typography variant="caption" color="text.secondary">Catatan kirim: {batch.catatanKirim}</Typography>}
              {batch.catatanSampai && <Typography variant="caption" color="text.secondary">Catatan diterima: {batch.catatanSampai}</Typography>}
              {batch.catatanPengiriman && !batch.catatanKirim && !batch.catatanSampai && (
                <Typography variant="caption" color="text.secondary">Catatan: {batch.catatanPengiriman}</Typography>
              )}
            </Stack>
          )}

          {/* Pembayaran per batch */}
          <Divider sx={{ my: 1 }} />
          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" sx={{ gap: 0.5 }}>
            {isSupplier && batch.statusPembayaran !== 'Sudah Dibayar' && batch.statusPenagihan !== 'Ditagihkan' && (
              <Button size="small" variant="outlined" color="warning" startIcon={<RequestQuote />} onClick={handleTagih} sx={{ textTransform: 'none' }}>
                Minta Penagihan
              </Button>
            )}
            {batch.statusPenagihan === 'Ditagihkan' && batch.statusPembayaran !== 'Sudah Dibayar' && (
              <Chip size="small" color="warning" icon={<RequestQuote sx={{ fontSize: 14 }} />} label={`Ditagihkan ${formatDate(batch.tanggalPenagihan)}`} />
            )}
            {batch.statusPembayaran === 'Sudah Dibayar' && (
              <Chip size="small" color="success" icon={<DoneAll sx={{ fontSize: 14 }} />} label="Lunas" />
            )}
            {canManageBayar && (
              <Button size="small" variant={batch.statusPembayaran === 'Sudah Dibayar' ? 'outlined' : 'contained'}
                color={batch.statusPembayaran === 'Sudah Dibayar' ? 'error' : 'success'}
                onClick={() => handleBayar(batch.statusPembayaran === 'Sudah Dibayar' ? 'Belum Dibayar' : 'Sudah Dibayar')}
                sx={{ textTransform: 'none' }}>
                {batch.statusPembayaran === 'Sudah Dibayar' ? 'Batalkan Lunas' : 'Tandai Lunas'}
              </Button>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Card>
  );
};

// Modal buat batch baru
const CreateBatchModal = ({ open, onClose, allDetails, existingBatchItemIds, onCreate, isSupplier }) => {
  const [selected, setSelected] = useState(new Set());
  const [saving, setSaving] = useState(false);

  const available = allDetails.filter(d => !existingBatchItemIds.has(d.id));

  const toggle = (id) => setSelected(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const handleCreate = async () => {
    if (selected.size === 0) return;
    setSaving(true);
    try {
      await onCreate([...selected]);
      setSelected(new Set());
      onClose();
    } catch (e) { /* handled by parent */ }
    finally { setSaving(false); }
  };

  if (!open) return null;

  return (
    <Card variant="outlined" sx={{ mb: 2, border: '2px dashed #1976d2' }}>
      <CardContent sx={{ p: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
          <Add sx={{ verticalAlign: 'middle', mr: 0.5 }} /> Buat Pengiriman Baru
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Pilih barang yang belum dikirim untuk batch pengiriman baru.
        </Typography>
        {available.length === 0 ? (
          <Alert severity="info" sx={{ py: 0.5 }}>Semua barang sudah dikirim.</Alert>
        ) : (
          <>
            <Stack spacing={0.25} sx={{ mb: 1.5 }}>
              {available.map((d) => (
                <Stack key={d.id} direction="row" alignItems="center" spacing={0.5}
                  sx={{ py: 0.5, px: 1, borderRadius: 1, bgcolor: selected.has(d.id) ? '#e3f2fd' : '#f5f5f5' }}>
                  <Checkbox size="small" checked={selected.has(d.id)} onChange={() => toggle(d.id)} />
                  <Typography variant="body2" sx={{ flex: 1 }}>{d.namaBarang}</Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                    {d.kategoriPembelian && <Chip size="small" label={d.kategoriPembelian} variant="outlined" color="primary" sx={{ fontSize: 9, height: 18 }} />}
                    <Typography variant="caption" color="text.secondary">{d.jumlahBarang} {d.satuan}</Typography>
                  </Stack>
                  <Typography variant="caption" fontWeight={600}>{formatRp(d.totalTransaksi)}</Typography>
                </Stack>
              ))}
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button variant="contained" size="small" disabled={saving || selected.size === 0} onClick={handleCreate} startIcon={<CheckCircle />}>
                {saving ? 'Membuat...' : 'Buat Pengiriman'}
              </Button>
              <Button variant="outlined" size="small" onClick={onClose}>Batal</Button>
            </Stack>
          </>
        )}
      </CardContent>
    </Card>
  );
};

const SupplierPengiriman = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:800px)');
  const user = useSelector((s) => s.authAdmin?.user);
  const userRole = user?.role || '';
  const isSupplier = userRole === 'Supplier Penyedia';
  const canManageBayar = ['admin', 'TFL', 'PPK', 'Kabid', 'Kadis'].includes(userRole);

  const [data, setData] = useState(null);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showCreateBatch, setShowCreateBatch] = useState(false);
  const notify = (m, s = 'success') => setSnackbar({ open: true, message: m, severity: s });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [res1, res2] = await Promise.all([getPembelianById(id), getBatches(id)]);
      setData(res1.data?.data);
      setBatches(res2.data?.data || []);
    } catch (e) {
      notify(e.response?.data?.message || 'Gagal memuat data', 'error');
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreateBatch = async (itemIds) => {
    setSaving(true);
    try {
      await createBatch(id, itemIds);
      notify('Batch pengiriman baru dibuat');
      setShowCreateBatch(false);
      fetchData();
    } catch (e) {
      notify(e.response?.data?.message || 'Gagal membuat batch', 'error');
      throw e;
    } finally { setSaving(false); }
  };

  const contentStyle = isMobile ? {} : { marginLeft: '250px', marginRight: '20px' };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
  );

  if (!data) return (
    <Box sx={{ p: 3 }}>
      <Alert severity="error">Data pembelian tidak ditemukan.</Alert>
      <Button sx={{ mt: 2 }} startIcon={<ArrowBack />} onClick={() => navigate('/data-pembelian-supplier')}>Kembali</Button>
    </Box>
  );

  const b = data.Bantuan || {};
  const details = data.DetailTransaksi || [];
  const supplier = data.SupplierBarang || {};

  // Kumpulkan semua detailId yang sudah ada di batch
  const existingBatchItemIds = new Set();
  batches.forEach(batch => (batch.Items || []).forEach(it => existingBatchItemIds.add(it.SupplierTransaksiDetailId)));

  // Hitung total sudah dikirim & sudah dibayar
  const totalDikirim = batches.reduce((s, batch) => s + (Number(batch.totalBatch) || 0), 0);
  const totalLunas = batches.filter(b => b.statusPembayaran === 'Sudah Dibayar').reduce((s, b) => s + (Number(b.totalBatch) || 0), 0);
  const totalBelumLunas = batches.filter(b => b.statusPembayaran !== 'Sudah Dibayar').reduce((s, b) => s + (Number(b.totalBatch) || 0), 0);

  return (
    <div className="main-content" style={contentStyle}>
      <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: '#f5f7fa', minHeight: '100vh' }}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="h5" fontWeight={600}>
              <LocalShipping sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
              Proses Pengiriman #{data.id}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Dibuat TFL: {formatDate(data.createdAt)} · {supplier.namaSupplier || '-'}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh"><Button size="small" onClick={fetchData} sx={{ border: '1px solid #e0e0e0', bgcolor: '#fff' }}><Refresh fontSize="small" /></Button></Tooltip>
            <Button variant="outlined" size="small" startIcon={<ArrowBack />} onClick={() => navigate('/data-pembelian-supplier')}>Kembali</Button>
          </Stack>
        </Stack>

        {/* Info penerima + ringkasan */}
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Atas Nama (Penerima)</Typography>
                <Typography variant="subtitle2" fontWeight={700}>
                  <Person sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                  {b.namaLengkap || '-'}
                </Typography>
                {b.JenisBantuan && <Typography variant="caption" color="text.secondary">{b.JenisBantuan}</Typography>}
                {(b.desaKelurahan || b.kecamatan) && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    <LocationOn sx={{ fontSize: 12, verticalAlign: 'middle' }} /> {[b.desaKelurahan, b.kecamatan].filter(Boolean).join(', ')}
                  </Typography>
                )}
                {b.namaTFL && <Chip size="small" variant="outlined" label={`TFL: ${b.namaTFL}`} sx={{ mt: 0.5, fontSize: 11, height: 22 }} />}
                {b.namaAgr && <Chip size="small" variant="outlined" color="secondary" label={`AGR: ${b.namaAgr}`} sx={{ mt: 0.5, ml: 0.5, fontSize: 11, height: 22 }} />}
              </Grid>
              <Grid item xs={12} sm={6} sx={{ textAlign: { sm: 'right' } }}>
                <Typography variant="caption" color="text.secondary">Total Transaksi</Typography>
                <Typography variant="h6" fontWeight={700} color="primary.main">{formatRp(data.totalTransaksi)}</Typography>
                <Stack direction="row" spacing={0.5} sx={{ mt: 1, justifyContent: { sm: 'flex-end' }, flexWrap: 'wrap', gap: 0.5 }}>
                  <Chip size="small" label={`Dikirim: ${formatRp(totalDikirim)}`} color="info" variant="outlined" />
                  <Chip size="small" label={`Lunas: ${formatRp(totalLunas)}`} color="success" variant="outlined" />
                  <Chip size="small" label={`Belum: ${formatRp(totalBelumLunas)}`} color="error" variant="outlined" />
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Tombol buat pengiriman baru */}
        {isSupplier && existingBatchItemIds.size < details.length && (
          <Button variant="contained" size="small" startIcon={<Add />} onClick={() => setShowCreateBatch(true)} sx={{ mb: 2, textTransform: 'none' }}>
            Buat Pengiriman Baru (Sisa {details.length - existingBatchItemIds.size} barang)
          </Button>
        )}

        {/* Modal buat batch baru */}
        {showCreateBatch && (
          <CreateBatchModal
            open={showCreateBatch}
            onClose={() => setShowCreateBatch(false)}
            allDetails={details}
            existingBatchItemIds={existingBatchItemIds}
            onCreate={handleCreateBatch}
            isSupplier={isSupplier}
          />
        )}

        {/* Daftar batch pengiriman */}
        {batches.length === 0 ? (
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <LocalShipping sx={{ fontSize: 40, color: '#e0e0e0', mb: 1 }} />
              <Typography variant="body1" fontWeight={600}>Belum ada pengiriman</Typography>
              <Typography variant="body2" color="text.secondary">
                {isSupplier ? 'Klik "Buat Pengiriman Baru" untuk mulai mengirim barang.' : 'Supplier belum membuat pengiriman.'}
              </Typography>
            </CardContent>
          </Card>
        ) : (
          batches.map((batch) => (
            <BatchCard
              key={batch.id}
              batch={batch}
              isSupplier={isSupplier}
              canManageBayar={canManageBayar}
              onChanged={fetchData}
              notify={notify}
              allDetails={details}
            />
          ))
        )}

        <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))} variant="filled">
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </div>
  );
};

export default SupplierPengiriman;
